"use client"

import {useCallback, useEffect, useState} from "react"
import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card"
import {Button} from "@/components/ui/button"
import {Tabs, TabsContent, TabsList, TabsTrigger} from "@/components/ui/tabs"
import { ArrowUp, Clock, Hash, List, CircleNotch as Loader2, Play, ArrowsClockwise as RefreshCw, DeviceMobile as Smartphone, User, XCircle } from "@phosphor-icons/react"
import {apiRequestWithAuth, getStoredToken, isTokenValid} from "@/lib/api-config"
import {useToast} from "@/hooks/use-toast"
import {useAuth} from "@/contexts/auth-context"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import NotLoggedIn from "@/components/not-logged-in"

interface FreeTask {
  id: number
  name: string
  account: string
  taskType: string
  agent: string | null
}

interface LockTask {
  deviceToken: string
  account: {
    id: number
    name: string
    account: string
  }
  expirationTime: string
}

interface CoolDownTask {
  id: number
  name: string
  account: string
  password?: string
  freeze: number
  server: number
  taskType: string
  refresh: number
  agent: string | null
  createTime: string
  updateTime: string
  expireTime: string
  san: string
  config: any
  active: any
  notice: any
}

export default function TasksPage() {
  const { token: contextToken } = useAuth()
  const { toast } = useToast()
  const [activeTab, setActiveTab] = useState("pending")
  const [freeTasks, setFreeTasks] = useState<FreeTask[]>([])
  const [lockTasks, setLockTasks] = useState<LockTask[]>([])
  const [coolDownTasks, setCoolDownTasks] = useState<CoolDownTask[]>([])
  const [loading, setLoading] = useState({
    pending: false,
    inProgress: false,
    coolingDown: false,
  })

  const getToken = () => {
    return contextToken || getStoredToken()
  }

  const fetchTasks = useCallback(
    async (tab: string) => {
      const token = getToken()
      if (!token || !isTokenValid(token)) {
        toast({
          variant: "destructive",
          title: "认证失败",
          description: "请重新登录",
        })
        return
      }

      setLoading((prev) => ({ ...prev, [tab]: true }))
      try {
        let result: any
        if (tab === "pending") {
          result = await apiRequestWithAuth("/showFreeTaskList", token, { method: "GET" })
          if (result.code === 200) {
            setFreeTasks(result.data || [])
          } else {
            toast({ variant: "destructive", title: "获取待处理任务失败", description: result.msg })
          }
        } else if (tab === "inProgress") {
          result = await apiRequestWithAuth("/showLockTaskList", token, { method: "GET" })
          if (result.code === 200) {
            setLockTasks(result.data || [])
          } else {
            toast({ variant: "destructive", title: "获取进行中任务失败", description: result.msg })
          }
        } else if (tab === "coolingDown") {
          result = await apiRequestWithAuth("/showFreezeTaskList", token, { method: "GET" })
          if (result.code === 200) {
            setCoolDownTasks(result.data || [])
          } else {
            toast({ variant: "destructive", title: "获取冷却中任务失败", description: result.msg })
          }
        }
      } catch (error) {
        toast({
          variant: "destructive",
          title: "网络错误",
          description: error instanceof Error ? error.message : "无法连接到服务器",
        })
      } finally {
        setLoading((prev) => ({ ...prev, [tab]: false }))
      }
    },
    [contextToken, toast],
  )

  useEffect(() => {
    const token = getToken()
    if (token && isTokenValid(token)) {
      // 并行请求三个独立的任务列表：总耗时取最慢者，而非串行累加
      Promise.all([
        fetchTasks("pending"),
        fetchTasks("inProgress"),
        fetchTasks("coolingDown"),
      ])
    }
  }, [contextToken, fetchTasks])

  const handleRefresh = () => {
    fetchTasks(activeTab)
  }

  const handleTempInsertTask = async (id: number) => {
    const token = getToken()
    if (!token || !isTokenValid(token)) return

    setLoading((prev) => ({ ...prev, pending: true }))
    try {
      const result = await apiRequestWithAuth("/tempInsertTask", token, {
        method: "POST",
        body: JSON.stringify({ id }),
      })
      if (result.code === 200) {
        toast({ variant: "success", title: "操作成功", description: `任务 ${id} 已插队` })
        await fetchTasks("pending")
        await fetchTasks("inProgress")
      } else {
        toast({ variant: "destructive", title: "操作失败", description: result.msg || `无法插队任务 ${id}` })
      }
    } catch (error) {
      toast({ variant: "destructive", title: "网络错误", description: "操作失败" })
    } finally {
      setLoading((prev) => ({ ...prev, pending: false }))
    }
  }

  const handleTempRemoveTask = async (id: number, type: "pending" | "coolingDown") => {
    const token = getToken()
    if (!token || !isTokenValid(token)) return

    setLoading((prev) => ({ ...prev, [type]: true }))
    try {
      const result = await apiRequestWithAuth("/tempRemoveTask", token, {
        method: "POST",
        body: JSON.stringify({ id }),
      })
      if (result.code === 200) {
        toast({ variant: "success", title: "操作成功", description: `任务 ${id} 已移除` })
        await fetchTasks(type)
      } else {
        toast({ variant: "destructive", title: "操作失败", description: result.msg || `无法移除任务 ${id}` })
      }
    } catch (error) {
      toast({ variant: "destructive", title: "网络错误", description: "操作失败" })
    } finally {
      setLoading((prev) => ({ ...prev, [type]: false }))
    }
  }

  const handleForceUnlockOneTask = async (tokenValue: string) => {
    const token = getToken()
    if (!token || !isTokenValid(token)) return

    setLoading((prev) => ({ ...prev, inProgress: true }))
    try {
      const result = await apiRequestWithAuth("/forceUnlockOneTask", token, {
        method: "POST",
        body: JSON.stringify({ token: tokenValue }),
      })
      if (result.code === 200) {
        toast({ variant: "success", title: "操作成功", description: `设备 ${tokenValue} 任务已移除` })
        await fetchTasks("inProgress")
      } else {
        toast({
          variant: "destructive",
          title: "操作失败",
          description: result.msg || `无法移除设备 ${tokenValue} 任务`,
        })
      }
    } catch (error) {
      toast({ variant: "destructive", title: "网络错误", description: "操作失败" })
    } finally {
      setLoading((prev) => ({ ...prev, inProgress: false }))
    }
  }

  const handleForceUnlockAllTasks = async () => {
    const token = getToken()
    if (!token || !isTokenValid(token)) return

    setLoading((prev) => ({ ...prev, inProgress: true }))
    try {
      const result = await apiRequestWithAuth("/forceUnlockTaskList", token, {
        method: "POST",
      })
      if (result.code === 200) {
        toast({ variant: "success", title: "操作成功", description: "所有进行中任务已移除" })
        await fetchTasks("inProgress")
      } else {
        toast({ variant: "destructive", title: "操作失败", description: result.msg || "无法移除所有进行中任务" })
      }
    } catch (error) {
      toast({ variant: "destructive", title: "网络错误", description: "操作失败" })
    } finally {
      setLoading((prev) => ({ ...prev, inProgress: false }))
    }
  }

  const handleForceLoadAllTasks = async () => {
    const token = getToken()
    if (!token || !isTokenValid(token)) return

    setLoading((prev) => ({ ...prev, pending: true }))
    try {
      const result = await apiRequestWithAuth("/forceLoadAllTask", token, {
        method: "POST",
      })
      if (result.code === 200) {
        toast({ variant: "success", title: "操作成功", description: "所有任务已强制同步" })
        await fetchTasks("pending")
      } else {
        toast({ variant: "destructive", title: "操作失败", description: result.msg || "无法强制同步所有任务" })
      }
    } catch (error) {
      toast({ variant: "destructive", title: "网络错误", description: "操作失败" })
    } finally {
      setLoading((prev) => ({ ...prev, pending: false }))
    }
  }

  const handleStartCoolDownTask = async (task: CoolDownTask) => {
    const token = getToken()
    if (!token || !isTokenValid(token)) return

    setLoading((prev) => ({ ...prev, coolingDown: true }))
    try {
      const unfreezeResult = await apiRequestWithAuth("/updateAccount", token, {
        method: "POST",
        body: JSON.stringify({ id: task.id, freeze: 0 }),
      })

      if (unfreezeResult.code === 200) {
        toast({ variant: "success", title: "操作成功", description: `用户 ${task.name} 已解除冻结` })
        const startResult = await apiRequestWithAuth("/startAccountByAdmin", token, {
          method: "POST",
          body: JSON.stringify({ id: task.id }),
        })

        if (startResult.code === 200) {
          toast({ variant: "success", title: "操作成功", description: `用户 ${task.name} 已立即上号` })
          await fetchTasks("coolingDown")
          await fetchTasks("inProgress")
        } else {
          toast({
            variant: "destructive",
            title: "操作失败",
            description: startResult.msg || `无法立即上号用户 ${task.name}`,
          })
        }
      } else {
        toast({
          variant: "destructive",
          title: "操作失败",
          description: unfreezeResult.msg || `无法解除冻结用户 ${task.name}`,
        })
      }
    } catch (error) {
      toast({ variant: "destructive", title: "网络错误", description: "操作失败" })
    } finally {
      setLoading((prev) => ({ ...prev, coolingDown: false }))
    }
  }

  const handleRemoveCoolDownTask = async (task: CoolDownTask) => {
    const token = getToken()
    if (!token || !isTokenValid(token)) return

    setLoading((prev) => ({ ...prev, coolingDown: true }))
    try {
      const result = await apiRequestWithAuth("/updateAccount", token, {
        method: "POST",
        body: JSON.stringify({ id: task.id, freeze: 0 }),
      })
      if (result.code === 200) {
        toast({ variant: "success", title: "操作成功", description: `用户 ${task.name} 已解除冻结` })
        await fetchTasks("coolingDown")
      } else {
        toast({
          variant: "destructive",
          title: "操作失败",
          description: result.msg || `无法解除冻结用户 ${task.name}`,
        })
      }
    } catch (error) {
      toast({ variant: "destructive", title: "网络错误", description: "操作失败" })
    } finally {
      setLoading((prev) => ({ ...prev, coolingDown: false }))
    }
  }

  const getTaskTypeName = (taskType: string) => {
    const taskTypes: Record<string, string> = {
      daily: "日常任务",
      rogue: "肉鸽任务",
      sand_fire: "生息演算",
    }
    return taskTypes[taskType] || taskType
  }

  const token = getToken()
  if (!token || !isTokenValid(token)) {
    return (
      <NotLoggedIn bg="var(--soil-200)" accent="var(--soil-500)" text="var(--soil-700)" />
    )
  }

  return (
    <>
      <div className="mb-6">
        <div className="page-title-box">
          <div className="page-title">任务管理</div>
          <div className="page-subtitle">查看和管理当前系统中的任务队列</div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList aria-label="任务状态" className="admin-task-island">
          <TabsTrigger value="pending">待处理 ({freeTasks.length})</TabsTrigger>
          <TabsTrigger value="inProgress">进行中 ({lockTasks.length})</TabsTrigger>
          <TabsTrigger value="coolingDown">冷却中 ({coolDownTasks?.length ?? 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          <Card style={{ border: "1.5px solid var(--soil-500)", background: "radial-gradient(circle, rgba(154,131,90,0.18) 1.5px, transparent 1.5px), radial-gradient(circle, rgba(190,165,120,0.12) 1px, transparent 1px), var(--soil-200)", color: "var(--soil-700)" }} className=" border-soil-500">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-soil-700">待处理任务</CardTitle>
              <div className="flex gap-2">
                <Button onClick={handleRefresh} disabled={loading.pending} size="sm" variant="outline">
                  <RefreshCw className={loading.pending ? "mr-2 h-4 w-4 animate-spin" : "mr-2 h-4 w-4"} />
                  刷新
                </Button>
                <Button onClick={handleForceLoadAllTasks} disabled={loading.pending} size="sm" variant="default">
                  <RefreshCw className="mr-2 h-4 w-4" />
                  强制同步所有任务
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading.pending ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin mr-2" />
                  <span className="text-soil-700">加载中...</span>
                </div>
              ) : freeTasks.length > 0 ? (
                <div className="space-y-4">
                  {freeTasks.map((task) => (
                    <div
                      key={task.id}
                      className="border rounded-lg p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-soil-500"
                    >
                      <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-500" />
                          <span className="text-gray-500 text-soil-600">ID:</span>
                          <span className="font-medium text-soil-700">{task.id}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-500" /> {/* Icon added */}
                          <span className="text-gray-500 text-soil-600">名称:</span>
                          <span className="font-medium text-soil-700">{task.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Hash className="h-4 w-4 text-gray-500" /> {/* Icon added */}
                          <span className="text-gray-500 text-soil-600">账号:</span>
                          <span className="font-medium text-soil-700">{task.account}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <List className="h-4 w-4 text-gray-500" />
                          <span className="text-gray-500 text-soil-600">类型:</span>
                          <span className="font-medium text-soil-700">{getTaskTypeName(task.taskType)}</span>
                        </div>
                        {task.agent && (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500 text-soil-600">代理:</span>
                            <span className="font-medium text-soil-700">{task.agent}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 mt-4 md:mt-0">
                        <Button
                          size="sm"
                          onClick={() => handleTempInsertTask(task.id)}
                          disabled={loading.pending}
                          className="bg-blue-500 hover:bg-blue-600 text-white"
                        >
                          <ArrowUp className="mr-2 h-4 w-4" />
                          插队
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={loading.pending}
                          onClick={() => {
                            setFreeTasks((prev) => prev.filter((t) => t.id !== task.id))
                            handleTempRemoveTask(task.id, "pending")
                          }}
                        >
                          <XCircle className="mr-2 h-4 w-4" />
                          移除
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <List className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 text-soil-600">暂无待处理任务</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="inProgress">
          <Card style={{ border: "1.5px solid var(--soil-500)", background: "radial-gradient(circle, rgba(154,131,90,0.18) 1.5px, transparent 1.5px), radial-gradient(circle, rgba(190,165,120,0.12) 1px, transparent 1px), var(--soil-200)", color: "var(--soil-700)" }} className=" border-soil-500">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-soil-700">进行中任务</CardTitle>
              <div className="flex gap-2">
                <Button onClick={handleRefresh} disabled={loading.inProgress} size="sm" variant="outline">
                  <RefreshCw className={loading.inProgress ? "mr-2 h-4 w-4 animate-spin" : "mr-2 h-4 w-4"} />
                  刷新
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="destructive" disabled={loading.inProgress}>
                      <XCircle className="mr-2 h-4 w-4" />
                      移除全部
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="" style={{ background: "var(--soil-200)" }}>
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-soil-700">确认移除全部进行中任务？</AlertDialogTitle>
                      <AlertDialogDescription className="text-soil-600">
                        此操作将强制停止所有当前正在运行的任务。
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="border-soil-500 text-soil-700">取消</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleForceUnlockAllTasks}
                        className="bg-red-600 hover:bg-red-700 text-white"
                      >
                        确认移除
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardHeader>
            <CardContent>
              {loading.inProgress ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin mr-2" />
                  <span className="text-soil-700">加载中...</span>
                </div>
              ) : lockTasks.length > 0 ? (
                <div className="space-y-4">
                  {lockTasks.map((task) => (
                    <div
                      key={task.deviceToken}
                      className="border rounded-lg p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-soil-500"
                    >
                      <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                        <div className="flex items-center gap-2">
                          <Smartphone className="h-4 w-4 text-gray-500" />
                          <span className="text-gray-500 text-soil-600">设备Token:</span>
                          <span className="font-medium text-soil-700 break-all">{task.deviceToken}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-500" /> {/* Icon added */}
                          <span className="text-gray-500 text-soil-600">账户名称:</span>
                          <span className="font-medium text-soil-700">{task.account.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Hash className="h-4 w-4 text-gray-500" /> {/* Icon added */}
                          <span className="text-gray-500 text-soil-600">游戏账号:</span>
                          <span className="font-medium text-soil-700">{task.account.account}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-gray-500" />
                          <span className="text-gray-500 text-soil-600">到期时间:</span>
                          <span className="font-medium text-soil-700">
                            {new Date(task.expirationTime).toLocaleString("zh-CN")}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4 md:mt-0">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="destructive" disabled={loading.inProgress}>
                              <XCircle className="mr-2 h-4 w-4" />
                              移除任务
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="" style={{ background: "var(--soil-200)" }}>
                            <AlertDialogHeader>
                              <AlertDialogTitle className="text-soil-700">确认移除任务？</AlertDialogTitle>
                              <AlertDialogDescription className="text-soil-600">
                                此操作将强制停止设备 {task.deviceToken} 上的任务。
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="border-soil-500 text-soil-700">
                                取消
                              </AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleForceUnlockOneTask(task.deviceToken)}
                                className="bg-red-600 hover:bg-red-700 text-white"
                              >
                                移除
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <List className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 text-soil-600">暂无进行中任务</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="coolingDown">
          <Card style={{ border: "1.5px solid var(--soil-500)", background: "radial-gradient(circle, rgba(154,131,90,0.18) 1.5px, transparent 1.5px), radial-gradient(circle, rgba(190,165,120,0.12) 1px, transparent 1px), var(--soil-200)", color: "var(--soil-700)" }} className=" border-soil-500">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-soil-700">冷却中任务</CardTitle>
              <Button onClick={handleRefresh} disabled={loading.coolingDown} size="sm" variant="outline">
                <RefreshCw className={loading.coolingDown ? "mr-2 h-4 w-4 animate-spin" : "mr-2 h-4 w-4"} />
                刷新
              </Button>
            </CardHeader>
            <CardContent>
              {loading.coolingDown ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin mr-2" />
                  <span className="text-soil-700">加载中...</span>
                </div>
              ) : coolDownTasks.length > 0 ? (
                <div className="space-y-4">
                  {coolDownTasks.map((task) => (
                    <div
                      key={task.id}
                      className="border rounded-lg p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-soil-500"
                    >
                      <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-500" />
                          <span className="text-gray-500 text-soil-600">ID:</span>
                          <span className="font-medium text-soil-700">{task.id}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-500" />
                          <span className="text-gray-500 text-soil-600">名称:</span>
                          <span className="font-medium text-soil-700">{task.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Hash className="h-4 w-4 text-gray-500" />
                          <span className="text-gray-500 text-soil-600">账号:</span>
                          <span className="font-medium text-soil-700">{task.account}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <List className="h-4 w-4 text-gray-500" />
                          <span className="text-gray-500 text-soil-600">类型:</span>
                          <span className="font-medium text-soil-700">{getTaskTypeName(task.taskType)}</span>
                        </div>
                        {task.agent && (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500 text-soil-600">代理:</span>
                            <span className="font-medium text-soil-700">{task.agent}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 mt-4 md:mt-0">
                        <Button
                          size="sm"
                          onClick={() => handleStartCoolDownTask(task)}
                          disabled={loading.coolingDown}
                          className="bg-green-500 hover:bg-green-600 text-white"
                        >
                          <Play className="mr-2 h-4 w-4" />
                          立即作战
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="destructive" disabled={loading.coolingDown}>
                              <XCircle className="mr-2 h-4 w-4" />
                              移除
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="" style={{ background: "var(--soil-200)" }}>
                            <AlertDialogHeader>
                              <AlertDialogTitle className="text-soil-700">确认移除任务？</AlertDialogTitle>
                              <AlertDialogDescription className="text-soil-600">
                                此操作将解除用户 {task.name} ({task.account}) 的冻结状态。
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="border-soil-500 text-soil-700">
                                取消
                              </AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleRemoveCoolDownTask(task)}
                                className="bg-red-600 hover:bg-red-700 text-white"
                              >
                                移除
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <List className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 text-soil-600">暂无冷却中任务</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </>
  )
}
